import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ViewportSchemaRenderer } from './print/ViewportSchemaRenderer';
import { GostStamp, type StampData } from './print/GostStamp';
import { 
  Printer, 
  ZoomIn, 
  ZoomOut, 
  Maximize2, 
  Lock, 
  Unlock, 
  ArrowLeft, 
  Save, 
  Check
} from 'lucide-react';

interface PrintSheet {
  id: number;
  project_id: number;
  name: string;
  format: 'A4' | 'A3' | 'A2' | 'A1';
  orientation: 'portrait' | 'landscape';
  margin_mm: string | { top: number; right: number; bottom: number; left: number };
  order_index: number;
  preset_id?: number;
}

interface PrintViewport {
  id: number;
  sheet_id: number;
  schema_id?: number;
  schema_type: string;
  screen_x: number;
  screen_y: number;
  screen_width: number;
  screen_height: number;
  scale: number;
  show_frame: number;
  clip: number;
  is_locked?: number;
  z_index: number;
  name?: string;
  scroll_x: number;
  scroll_y: number;
  zoom: number;
  block_id?: number;
}

interface ElectricalSchema {
  id: number;
  project_id: number;
  name: string;
  schema_type_id: number;
  electrical_panel_id?: number;
}

// Format dimensions in millimeters
const SHEET_SIZES_MM: Record<string, { width: number; height: number }> = {
  A4: { width: 297, height: 210 },
  A3: { width: 420, height: 297 },
  A2: { width: 594, height: 420 },
  A1: { width: 841, height: 594 },
};

const MM_TO_PX = 3.779527559; // 96 DPI

export default function PrintSheetEditor() {
  const { projectId, sheetId } = useParams<{ projectId: string; sheetId: string }>();
  const queryClient = useQueryClient();

  const parsedSheetId = Number(sheetId);
  const parsedProjectId = Number(projectId);

  // States
  const [sheetScale, setSheetScale] = useState<number>(0.85);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const panOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const sheetScaleRef = useRef<number>(0.85);

  useEffect(() => {
    panOffsetRef.current = panOffset;
  }, [panOffset]);
  useEffect(() => {
    sheetScaleRef.current = sheetScale;
  }, [sheetScale]);

  const [activeViewportId, setActiveViewportId] = useState<number | null>(null);
  const [isModelSpaceActive, setIsModelSpaceActive] = useState<boolean>(false);
  const [isExportingPdf, setIsExportingPdf] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [sidebarTab, setSidebarTab] = useState<'viewport' | 'sheet' | 'stamp'>('viewport');

  // Debounce save ref
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sheetContainerRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);

  // Pan the sheet (by dark background drag or middle mouse button anywhere)
  const startSheetPan = useCallback((clientX: number, clientY: number) => {
    setIsPanning(true);
    const startX = clientX;
    const startY = clientY;
    const startPan = { ...panOffsetRef.current };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      const nextPan = {
        x: Math.round(startPan.x + dx),
        y: Math.round(startPan.y + dy),
      };
      panOffsetRef.current = nextPan;
      setPanOffset(nextPan);
    };

    const onMouseUp = () => {
      setIsPanning(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  // Wheel Zoom to Mouse Cursor for Paper Space (Sheet)
  useEffect(() => {
    const workspaceEl = workspaceRef.current;
    if (!workspaceEl) return;

    const handleWheel = (e: WheelEvent) => {
      // If currently interacting inside an active viewport model space, let React Flow handle it
      if (isModelSpaceActive) {
        const target = e.target as HTMLElement;
        if (target && target.closest(`#viewport-box-${activeViewportId}`)) {
          return;
        }
      }

      // Intercept and prevent native vertical scroll of the workspace
      e.preventDefault();

      const delta = e.deltaY;
      if (delta === 0) return;

      const rect = workspaceEl.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;

      const currentScale = sheetScaleRef.current;
      const currentPan = panOffsetRef.current;

      // Multiplicative smooth zoom: scroll up (delta < 0) zooms in, scroll down (delta > 0) zooms out
      const zoomFactor = delta < 0 ? 1.08 : 0.92;
      const nextScale = Math.min(3.0, Math.max(0.15, Number((currentScale * zoomFactor).toFixed(3))));
      if (nextScale === currentScale) return;

      const scaleRatio = nextScale / currentScale;

      // CAD zoom-to-cursor formula:
      // The point on the sheet under cursor remains exactly under the cursor
      const nextPanX = cursorX - scaleRatio * (cursorX - currentPan.x);
      const nextPanY = cursorY - scaleRatio * (cursorY - currentPan.y);

      const nextPan = {
        x: Math.round(nextPanX * 10) / 10,
        y: Math.round(nextPanY * 10) / 10,
      };

      sheetScaleRef.current = nextScale;
      panOffsetRef.current = nextPan;
      setSheetScale(nextScale);
      setPanOffset(nextPan);
    };

    workspaceEl.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      workspaceEl.removeEventListener('wheel', handleWheel);
    };
  }, [isModelSpaceActive, activeViewportId]);

  // Pan the workspace by dragging the dark background or middle mouse button
  const handleWorkspaceMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button === 1 || (e.button === 0 && e.target === workspaceRef.current)) {
      e.preventDefault();
      startSheetPan(e.clientX, e.clientY);
    }
  };

  // 1. Fetch current sheet
  const { data: sheetData, isLoading: isSheetLoading } = useQuery({
    queryKey: ['print-sheet', parsedSheetId],
    queryFn: async () => {
      const res = await fetch(`/api/print/sheets/${parsedSheetId}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load sheet');
      return data.data as PrintSheet;
    },
    enabled: !!parsedSheetId,
  });

  // 2. Fetch project sheets (to know order and total count)
  const { data: allSheets = [] } = useQuery({
    queryKey: ['print-sheets', parsedProjectId],
    queryFn: async () => {
      const res = await fetch(`/api/print/sheets?project_id=${parsedProjectId}`);
      const data = await res.json();
      return (data.success ? data.data : []) as PrintSheet[];
    },
    enabled: !!parsedProjectId,
  });

  // 3. Fetch viewports for this sheet
  const { data: viewports = [], isLoading: isViewportsLoading } = useQuery({
    queryKey: ['print-viewports', parsedSheetId],
    queryFn: async () => {
      const res = await fetch(`/api/print/viewports?sheet_id=${parsedSheetId}`);
      const data = await res.json();
      return (data.success ? data.data : []) as PrintViewport[];
    },
    enabled: !!parsedSheetId,
  });

  // 4. Fetch available schemas in this project
  const { data: projectSchemas = [] } = useQuery({
    queryKey: ['project-schemas', parsedProjectId],
    queryFn: async () => {
      const res = await fetch(`/api/schemas?project_id=${parsedProjectId}`);
      const data = await res.json();
      return (data.success ? data.data : []) as ElectricalSchema[];
    },
    enabled: !!parsedProjectId,
  });

  // 5. Fetch project details
  const { data: projectInfo } = useQuery({
    queryKey: ['project-info', parsedProjectId],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${parsedProjectId}`);
      const data = await res.json();
      return data.success ? data.data : null;
    },
    enabled: !!parsedProjectId,
  });

  // 6. Fetch sheet stamp
  const { data: stampRecord } = useQuery({
    queryKey: ['print-sheet-stamp', parsedSheetId],
    queryFn: async () => {
      const res = await fetch(`/api/print/sheets/${parsedSheetId}/stamp`);
      const data = await res.json();
      return data.success ? data.data : null;
    },
    enabled: !!parsedSheetId,
  });

  // Stamp state
  const [stampData, setStampData] = useState<StampData>({});
  const [stampType, setStampType] = useState<'gost_form1' | 'gost_form2a' | 'simple' | 'none'>('gost_form1');

  // Sheet numbers
  const sheetIndex = useMemo(() => {
    const idx = allSheets.findIndex(s => s.id === parsedSheetId);
    return idx >= 0 ? idx + 1 : 1;
  }, [allSheets, parsedSheetId]);

  const totalSheetsCount = allSheets.length || 1;

  // Initialize stamp data
  useEffect(() => {
    if (stampRecord?.values) {
      setStampData({
        projectName: projectInfo?.name || '',
        sheetTitle: sheetData?.name || '',
        sheetNumber: sheetIndex,
        totalSheets: totalSheetsCount,
        ...stampRecord.values,
      });
      const savedType = stampRecord.values?.stampType;
      if (savedType && ['gost_form1', 'gost_form2a', 'simple', 'none'].includes(savedType)) {
        setStampType(savedType);
      } else if (stampRecord.stamp_code) {
        if (stampRecord.stamp_code === 'gost_form2a' || stampRecord.stamp_code.includes('form2a')) {
          setStampType('gost_form2a');
        } else if (stampRecord.stamp_code === 'simple' || stampRecord.stamp_code.includes('simple')) {
          setStampType('simple');
        } else if (stampRecord.stamp_code === 'none') {
          setStampType('none');
        } else {
          setStampType('gost_form1');
        }
      } else if (stampRecord.stamp_id) {
        if (stampRecord.stamp_id === 3) setStampType('gost_form2a');
        else if (stampRecord.stamp_id === 2) setStampType('simple');
        else if (stampRecord.stamp_id === 4) setStampType('none');
        else setStampType('gost_form1');
      }
    } else if (sheetData || projectInfo) {
      setStampData(prev => ({
        ...prev,
        projectName: prev.projectName || projectInfo?.name || 'Объект',
        sheetTitle: prev.sheetTitle || sheetData?.name || 'Схема принципиальная',
        sheetNumber: sheetIndex,
        totalSheets: totalSheetsCount,
        docCode: prev.docCode || 'ЭОМ-01',
        stage: prev.stage || 'Р',
        company: prev.company || 'BAGSUS',
        author: prev.author || 'Инженер',
        checker: prev.checker || 'ГИП',
        date: prev.date || new Date().toLocaleDateString('ru-RU'),
      }));
    }
  }, [stampRecord, sheetData, projectInfo, sheetIndex, totalSheetsCount]);

  // Selected viewport
  const currentViewport = useMemo(() => {
    if (viewports.length === 0) return null;
    if (activeViewportId) {
      const found = viewports.find(v => v.id === activeViewportId);
      if (found) return found;
    }
    return viewports[0];
  }, [viewports, activeViewportId]);

  // Selected viewport local controls
  const [localPanX, setLocalPanX] = useState<number>(0);
  const [localPanY, setLocalPanY] = useState<number>(0);
  const [localZoom, setLocalZoom] = useState<number>(1);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [selectedSchemaId, setSelectedSchemaId] = useState<number | undefined>(undefined);

  // Local geometry controls (in mm)
  const [geoX, setGeoX] = useState<number>(20);
  const [geoY, setGeoY] = useState<number>(5);
  const [geoW, setGeoW] = useState<number>(272);
  const [geoH, setGeoH] = useState<number>(145);

  useEffect(() => {
    if (currentViewport) {
      setLocalPanX(Number(currentViewport.scroll_x) || 0);
      setLocalPanY(Number(currentViewport.scroll_y) || 0);
      setLocalZoom(Number(currentViewport.zoom) || 1);
      setIsLocked(!!currentViewport.is_locked);
      setSelectedSchemaId(currentViewport.schema_id || (projectSchemas[0]?.id));

      setGeoX(Math.round(currentViewport.screen_x || 20));
      setGeoY(Math.round(currentViewport.screen_y || 5));
      setGeoW(Math.round(currentViewport.screen_width || 272));
      setGeoH(Math.round(currentViewport.screen_height || 145));
    }
  }, [currentViewport, projectSchemas]);

  // Sheet dimensions
  const format = sheetData?.format || 'A4';
  const isLandscape = (sheetData?.orientation || 'landscape') === 'landscape';
  const mmSizes = SHEET_SIZES_MM[format] || SHEET_SIZES_MM.A4;
  const sheetWidthMm = isLandscape ? mmSizes.width : mmSizes.height;
  const sheetHeightMm = isLandscape ? mmSizes.height : mmSizes.width;

  // Center sheet on initial load
  const hasInitializedPan = useRef<boolean>(false);
  useEffect(() => {
    if (hasInitializedPan.current || !workspaceRef.current || isSheetLoading) return;
    const ws = workspaceRef.current;
    const wsW = ws.clientWidth;
    const wsH = ws.clientHeight;
    if (wsW > 0 && wsH > 0) {
      const sheetWpx = sheetWidthMm * MM_TO_PX;
      const sheetHpx = sheetHeightMm * MM_TO_PX;
      const fitScale = Math.min(
        1.0,
        Math.max(0.2, Math.min((wsW - 120) / sheetWpx, (wsH - 120) / sheetHpx))
      );
      const roundedScale = Number(fitScale.toFixed(2));
      setSheetScale(roundedScale);
      sheetScaleRef.current = roundedScale;

      const initX = Math.round((wsW - sheetWpx * roundedScale) / 2);
      const initY = Math.round((wsH - sheetHpx * roundedScale) / 2);
      setPanOffset({ x: initX, y: initY });
      panOffsetRef.current = { x: initX, y: initY };
      hasInitializedPan.current = true;
    }
  }, [isSheetLoading, sheetWidthMm, sheetHeightMm]);

  // Auto-create default viewport if sheet has no viewports
  useEffect(() => {
    if (!isViewportsLoading && viewports.length === 0 && sheetData && projectSchemas.length > 0) {
      const firstSchema = projectSchemas.find(s => s.id === 137) || projectSchemas[0];
      const stampHeightMm = stampType === 'gost_form1' ? 55 : (stampType === 'gost_form2a' ? 15 : (stampType === 'simple' ? 25 : 0));
      const defaultW = Math.max(50, sheetWidthMm - 25);
      const defaultH = Math.max(50, sheetHeightMm - 10 - stampHeightMm);

      const createDefaultViewport = async () => {
        try {
          await fetch('/api/print/viewports', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sheet_id: parsedSheetId,
              schema_id: firstSchema.id,
              schema_type: 'main',
              screen_x: 20,
              screen_y: 5,
              screen_width: defaultW,
              screen_height: defaultH,
              scale: 1,
              show_frame: 1,
              clip: 1,
              is_locked: 0,
              scroll_x: 0,
              scroll_y: 0,
              zoom: 1.0,
            }),
          });
          queryClient.invalidateQueries({ queryKey: ['print-viewports', parsedSheetId] });
        } catch (e) {
          console.error('Failed to create default viewport:', e);
        }
      };
      createDefaultViewport();
    }
  }, [isViewportsLoading, viewports.length, sheetData, projectSchemas, parsedSheetId, queryClient, sheetWidthMm, sheetHeightMm, stampType]);

  // Save viewport changes to API
  const saveViewport = useCallback(async (vpId: number, updates: Partial<PrintViewport>) => {
    setSaveStatus('Сохранение...');
    try {
      const res = await fetch(`/api/print/viewports/${vpId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.success) {
        setSaveStatus('Сохранено');
        setTimeout(() => setSaveStatus(''), 2000);
        queryClient.invalidateQueries({ queryKey: ['print-viewports', parsedSheetId] });
      } else {
        setSaveStatus('Ошибка сохранения');
      }
    } catch (err) {
      console.error('Error saving viewport:', err);
      setSaveStatus('Ошибка');
    }
  }, [parsedSheetId, queryClient]);

  // Debounced auto-save for pan/zoom
  const scheduleSave = useCallback((vpId: number, updates: Partial<PrintViewport>) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveViewport(vpId, updates);
    }, 400);
  }, [saveViewport]);

  // Fit Viewport to Sheet Dimensions (AutoCAD Fit)
  const handleFitViewportToSheet = useCallback((customFormat?: string, customOrientation?: string) => {
    if (!currentViewport) return;
    const fmt = customFormat || sheetData?.format || 'A4';
    const orient = customOrientation || sheetData?.orientation || 'landscape';
    const isLand = orient === 'landscape';
    const sizes = SHEET_SIZES_MM[fmt] || SHEET_SIZES_MM.A4;
    const sW = isLand ? sizes.width : sizes.height;
    const sH = isLand ? sizes.height : sizes.width;

    const stampHeightMm = stampType === 'gost_form1' ? 55 : (stampType === 'gost_form2a' ? 15 : (stampType === 'simple' ? 25 : 0));
    const newX = 20; // 20mm left margin (binding)
    const newY = 5;  // 5mm top margin
    const newW = Math.max(50, sW - 25); // 20mm left + 5mm right
    const newH = Math.max(50, sH - 10 - stampHeightMm); // 5mm top + 5mm bottom + stamp

    setGeoX(newX);
    setGeoY(newY);
    setGeoW(newW);
    setGeoH(newH);

    saveViewport(currentViewport.id, {
      screen_x: newX,
      screen_y: newY,
      screen_width: newW,
      screen_height: newH,
    });
  }, [currentViewport, sheetData, stampType, saveViewport]);

  // Apply geometry manually
  const applyGeometry = (x: number, y: number, w: number, h: number) => {
    setGeoX(x);
    setGeoY(y);
    setGeoW(w);
    setGeoH(h);
    if (currentViewport) {
      saveViewport(currentViewport.id, {
        screen_x: x,
        screen_y: y,
        screen_width: w,
        screen_height: h,
      });
    }
  };

  // Interactive Resizing on Paper Canvas
  const resizeStateRef = useRef<{
    handle: string;
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
    initialW: number;
    initialH: number;
    vpId: number;
  } | null>(null);

  const handleResizeStart = (e: React.MouseEvent, handle: string, vp: PrintViewport) => {
    // If middle mouse button, trigger whole sheet PAN instead of moving/resizing viewport
    if (e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      startSheetPan(e.clientX, e.clientY);
      return;
    }

    // Only allow left mouse button for viewport move/resize
    if (e.button !== 0) return;

    e.stopPropagation();
    e.preventDefault();

    resizeStateRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      initialX: vp.screen_x,
      initialY: vp.screen_y,
      initialW: vp.screen_width,
      initialH: vp.screen_height,
      vpId: vp.id,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizeStateRef.current) return;
      const st = resizeStateRef.current;
      const dxPx = moveEvent.clientX - st.startX;
      const dyPx = moveEvent.clientY - st.startY;

      // Convert screen px to mm using scale
      const dxMm = dxPx / (MM_TO_PX * sheetScale);
      const dyMm = dyPx / (MM_TO_PX * sheetScale);

      let newX = st.initialX;
      let newY = st.initialY;
      let newW = st.initialW;
      let newH = st.initialH;

      if (st.handle === 'move') {
        newX = Math.max(0, Math.min(sheetWidthMm - newW, Math.round(st.initialX + dxMm)));
        newY = Math.max(0, Math.min(sheetHeightMm - newH, Math.round(st.initialY + dyMm)));
      } else {
        if (st.handle.includes('e')) {
          newW = Math.max(40, Math.min(sheetWidthMm - newX, Math.round(st.initialW + dxMm)));
        }
        if (st.handle.includes('s')) {
          newH = Math.max(30, Math.min(sheetHeightMm - newY, Math.round(st.initialH + dyMm)));
        }
        if (st.handle.includes('w')) {
          const possibleW = Math.max(40, Math.round(st.initialW - dxMm));
          newX = Math.max(0, Math.round(st.initialX + (st.initialW - possibleW)));
          newW = possibleW;
        }
        if (st.handle.includes('n')) {
          const possibleH = Math.max(30, Math.round(st.initialH - dyMm));
          newY = Math.max(0, Math.round(st.initialY + (st.initialH - possibleH)));
          newH = possibleH;
        }
      }

      setGeoX(newX);
      setGeoY(newY);
      setGeoW(newW);
      setGeoH(newH);
    };

    const handleMouseUp = () => {
      if (resizeStateRef.current) {
        const vpId = resizeStateRef.current.vpId;
        saveViewport(vpId, {
          screen_x: geoX,
          screen_y: geoY,
          screen_width: geoW,
          screen_height: geoH,
        });
        resizeStateRef.current = null;
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Handle interactive viewport pan/zoom change
  const handleViewportPanZoomChange = useCallback((updates: { scroll_x: number; scroll_y: number; zoom: number }) => {
    setLocalPanX(Math.round(updates.scroll_x));
    setLocalPanY(Math.round(updates.scroll_y));
    setLocalZoom(Number(updates.zoom.toFixed(4)));

    if (currentViewport) {
      scheduleSave(currentViewport.id, {
        scroll_x: updates.scroll_x,
        scroll_y: updates.scroll_y,
        zoom: updates.zoom,
      });
    }
  }, [currentViewport, scheduleSave]);

  // Manual numeric pan/zoom change
  const applyManualPanZoom = (newPanX: number, newPanY: number, newZoom: number) => {
    setLocalPanX(newPanX);
    setLocalPanY(newPanY);
    setLocalZoom(newZoom);
    if (currentViewport) {
      saveViewport(currentViewport.id, {
        scroll_x: newPanX,
        scroll_y: newPanY,
        zoom: newZoom,
      });
    }
  };

  // Schema change
  const handleSelectSchema = (schemaIdNum: number) => {
    setSelectedSchemaId(schemaIdNum);
    if (currentViewport) {
      saveViewport(currentViewport.id, {
        schema_id: schemaIdNum,
      });
    }
  };

  // Lock toggle
  const handleToggleLock = () => {
    const nextLocked = !isLocked;
    setIsLocked(nextLocked);
    if (currentViewport) {
      saveViewport(currentViewport.id, {
        is_locked: nextLocked ? 1 : 0,
      });
    }
  };

  // Bounds computed: Zoom Extents
  const computedBoundsRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);
  const handleBoundsComputed = useCallback((bounds: { minX: number; minY: number; maxX: number; maxY: number }) => {
    computedBoundsRef.current = bounds;
  }, []);

  const handleZoomExtents = () => {
    const b = computedBoundsRef.current;
    if (!b || !currentViewport) return;

    const padding = 100;
    const schemaW = (b.maxX - b.minX) + padding * 2;
    const schemaH = (b.maxY - b.minY) + padding * 2;
    if (schemaW <= 0 || schemaH <= 0) return;

    const vpElement = document.getElementById(`viewport-box-${currentViewport.id}`);
    const vpW = vpElement ? vpElement.clientWidth : 800;
    const vpH = vpElement ? vpElement.clientHeight : 500;

    const scaleX = vpW / schemaW;
    const scaleY = vpH / schemaH;
    const fitZoom = Math.min(Math.max(Math.min(scaleX, scaleY), 0.05), 3.0);

    const centerX = (b.minX + b.maxX) / 2;
    const centerY = (b.minY + b.maxY) / 2;

    const targetScrollX = Math.round(centerX - vpW / (2 * fitZoom));
    const targetScrollY = Math.round(centerY - vpH / (2 * fitZoom));

    applyManualPanZoom(targetScrollX, targetScrollY, Number(fitZoom.toFixed(4)));
  };

  // Save Stamp
  const handleSaveStamp = useCallback(async (typeToSave?: 'gost_form1' | 'gost_form2a' | 'simple' | 'none', dataToSave?: StampData) => {
    const actualType = typeToSave || stampType;
    const actualData = dataToSave || stampData;
    const stampIdMap: Record<string, number> = {
      gost_form1: 1,
      simple: 2,
      gost_form2a: 3,
      none: 4,
    };
    setSaveStatus('Сохранение штампа...');
    try {
      await fetch(`/api/print/sheets/${parsedSheetId}/stamp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stamp_id: stampIdMap[actualType] || 1,
          stamp_code: actualType,
          values: {
            ...actualData,
            stampType: actualType,
          },
        }),
      });
      setSaveStatus('Штамп сохранен');
      setTimeout(() => setSaveStatus(''), 2000);
      queryClient.invalidateQueries({ queryKey: ['print-sheet-stamp', parsedSheetId] });
    } catch (e) {
      console.error(e);
      setSaveStatus('Ошибка сохранения штампа');
    }
  }, [stampType, stampData, parsedSheetId, queryClient]);

  // Export Sheet to PDF
  const handleExportPdf = async () => {
    if (!sheetContainerRef.current) return;
    setIsExportingPdf(true);
    setSaveStatus('Подготовка экспорта...');

    try {
      const { toPng } = await import('html-to-image');
      const { jsPDF } = await import('jspdf');

      const formatPdf = sheetData?.format || 'A4';
      const isLandscapePdf = (sheetData?.orientation || 'landscape') === 'landscape';

      const sizesPdf = SHEET_SIZES_MM[formatPdf] || SHEET_SIZES_MM.A4;
      const pdfW = isLandscapePdf ? sizesPdf.width : sizesPdf.height;
      const pdfH = isLandscapePdf ? sizesPdf.height : sizesPdf.width;

      const element = sheetContainerRef.current;
      const sheetWpx = Math.round(pdfW * MM_TO_PX);
      const sheetHpx = Math.round(pdfH * MM_TO_PX);

      // Give React a tick to re-render with isExportingPdf = true (hiding badges, handles, resetting transform)
      await new Promise((resolve) => setTimeout(resolve, 200));

      setSaveStatus('Рендеринг чертежа...');

      const imgData = await toPng(element, {
        pixelRatio: 2, // 300 DPI equivalent
        backgroundColor: '#ffffff',
        width: sheetWpx,
        height: sheetHpx,
        skipFonts: true,
        style: {
          transform: 'none',
          left: '0px',
          top: '0px',
          boxShadow: 'none',
        },
        filter: (domNode: HTMLElement) => {
          if (domNode.classList) {
            if (
              domNode.classList.contains('cursor-nwse-resize') ||
              domNode.classList.contains('cursor-nesw-resize') ||
              domNode.classList.contains('cursor-ns-resize') ||
              domNode.classList.contains('cursor-ew-resize') ||
              domNode.classList.contains('react-flow__attribution')
            ) {
              return false;
            }
          }
          return true;
        },
      });

      setSaveStatus('Формирование PDF...');

      const pdf = new jsPDF({
        orientation: isLandscapePdf ? 'landscape' : 'portrait',
        unit: 'mm',
        format: formatPdf.toLowerCase() as any,
      });

      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH, undefined, 'FAST');

      const filename = `${sheetData?.name || 'Лист'}_${parsedSheetId}.pdf`;
      pdf.save(filename);

      const pdfBase64 = pdf.output('datauristring');
      await fetch('/api/print/pdf-files', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: parsedProjectId,
          filename,
          description: `Экспорт листа "${sheetData?.name || ''}" (${formatPdf})`,
          pdf_base64: pdfBase64,
        }),
      });

      setSaveStatus('PDF успешно создан');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (err) {
      console.error('Export error:', err);
      setSaveStatus('Ошибка экспорта PDF');
    } finally {
      setIsExportingPdf(false);
    }
  };

  if (isSheetLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3"></div>
          <p className="text-gray-400">Загрузка листа чертежа...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-900 text-gray-100 overflow-hidden font-sans">
      {/* 1. Верхняя панель (CAD Toolbar) */}
      <header className="h-14 bg-gray-800 border-b border-gray-700 px-4 flex items-center justify-between shrink-0 z-30">
        <div className="flex items-center space-x-3">
          <Link
            to={`/projects/${parsedProjectId}/prints`}
            className="flex items-center space-x-1 text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-700 transition"
            title="Назад к книге листов"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Книга листов</span>
          </Link>
          <div className="h-5 w-px bg-gray-700" />
          <h1 className="text-sm font-bold text-white flex items-center space-x-2">
            <span className="text-blue-400">Лист {sheetIndex}:</span>
            <span>{sheetData?.name || 'Лист чертежа'}</span>
            <span className="text-xs font-normal text-gray-400 bg-gray-700 px-2 py-0.5 rounded">
              {format} {isLandscape ? 'альбомный' : 'книжный'} ({sheetWidthMm}×{sheetHeightMm} мм)
            </span>
          </h1>
        </div>

        {/* Инструменты масштаба листа и экспорт */}
        <div className="flex items-center space-x-3">
          {saveStatus && (
            <span className="text-xs text-yellow-400 flex items-center space-x-1 animate-pulse">
              <Check className="w-3 h-3" />
              <span>{saveStatus}</span>
            </span>
          )}

          {/* Кнопка "Растянуть видовой экран на весь лист" */}
          {currentViewport && (
            <button
              onClick={() => handleFitViewportToSheet()}
              className="flex items-center space-x-1 bg-gray-700 hover:bg-gray-600 text-gray-200 px-2.5 py-1.5 rounded text-xs transition border border-gray-600"
              title="Растянуть видовой экран на весь лист (с отступами ГОСТ)"
            >
              <Maximize2 className="w-3.5 h-3.5 text-blue-400" />
              <span>Растянуть на весь лист</span>
            </button>
          )}

          {/* Масштаб листа на экране */}
          <div className="flex items-center bg-gray-700 rounded p-0.5 space-x-1">
            <button
              onClick={() => {
                if (!workspaceRef.current) return;
                const ws = workspaceRef.current;
                const cursorX = ws.clientWidth / 2;
                const cursorY = ws.clientHeight / 2;
                const currentScale = sheetScaleRef.current;
                const nextScale = Math.max(0.15, Number((currentScale * 0.85).toFixed(3)));
                const ratio = nextScale / currentScale;
                const nextPan = {
                  x: Math.round(cursorX - ratio * (cursorX - panOffsetRef.current.x)),
                  y: Math.round(cursorY - ratio * (cursorY - panOffsetRef.current.y)),
                };
                sheetScaleRef.current = nextScale;
                panOffsetRef.current = nextPan;
                setSheetScale(nextScale);
                setPanOffset(nextPan);
              }}
              className="p-1 hover:bg-gray-600 rounded text-gray-300"
              title="Уменьшить лист"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs font-mono px-1 text-gray-300 min-w-[42px] text-center">
              {Math.round(sheetScale * 100)}%
            </span>
            <button
              onClick={() => {
                if (!workspaceRef.current) return;
                const ws = workspaceRef.current;
                const cursorX = ws.clientWidth / 2;
                const cursorY = ws.clientHeight / 2;
                const currentScale = sheetScaleRef.current;
                const nextScale = Math.min(3.0, Number((currentScale * 1.15).toFixed(3)));
                const ratio = nextScale / currentScale;
                const nextPan = {
                  x: Math.round(cursorX - ratio * (cursorX - panOffsetRef.current.x)),
                  y: Math.round(cursorY - ratio * (cursorY - panOffsetRef.current.y)),
                };
                sheetScaleRef.current = nextScale;
                panOffsetRef.current = nextPan;
                setSheetScale(nextScale);
                setPanOffset(nextPan);
              }}
              className="p-1 hover:bg-gray-600 rounded text-gray-300"
              title="Увеличить лист"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => {
                if (!workspaceRef.current) return;
                const ws = workspaceRef.current;
                const sheetWpx = sheetWidthMm * MM_TO_PX;
                const sheetHpx = sheetHeightMm * MM_TO_PX;
                const targetScale = 1.0;
                const nextPan = {
                  x: Math.round((ws.clientWidth - sheetWpx * targetScale) / 2),
                  y: Math.round((ws.clientHeight - sheetHpx * targetScale) / 2),
                };
                sheetScaleRef.current = targetScale;
                panOffsetRef.current = nextPan;
                setSheetScale(targetScale);
                setPanOffset(nextPan);
              }}
              className="text-[10px] px-1.5 py-0.5 hover:bg-gray-600 rounded text-gray-300 border-l border-gray-600"
              title="100% размер листа и центрирование"
            >
              100%
            </button>
          </div>

          <button
            onClick={handleExportPdf}
            disabled={isExportingPdf}
            className="flex items-center space-x-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white px-3 py-1.5 rounded text-xs font-semibold shadow transition"
          >
            <Printer className="w-4 h-4" />
            <span>{isExportingPdf ? 'Подготовка PDF...' : 'Экспорт в PDF'}</span>
          </button>
        </div>
      </header>

      {/* 2. Основная рабочая область (Холст + Правая панель) */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Рабочий стол чертежа (Dark CAD Workspace) */}
        <div
          ref={workspaceRef}
          className={`flex-1 overflow-hidden bg-[#1a1d24] relative select-none ${
            isPanning ? 'cursor-grabbing' : ''
          }`}
          onMouseDown={handleWorkspaceMouseDown}
          onClick={() => {
            // Клик снаружи деактивирует видовой экран
            if (activeViewportId !== null) {
              setActiveViewportId(null);
              setIsModelSpaceActive(false);
            }
          }}
        >
          {/* Контейнер листа в масштабе sheetScale с CAD panOffset */}
          <div
            ref={sheetContainerRef}
            id="print-sheet-paper"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: `${sheetWidthMm}mm`,
              height: `${sheetHeightMm}mm`,
              transform: isExportingPdf ? 'none' : `translate(${panOffset.x}px, ${panOffset.y}px) scale(${sheetScale})`,
              transformOrigin: '0 0',
              boxSizing: 'border-box',
              boxShadow: isExportingPdf ? 'none' : '0 10px 40px rgba(0,0,0,0.6)',
              willChange: isPanning ? 'transform' : 'auto',
            }}
            className="bg-white text-black select-none shrink-0"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              if (e.button === 1) {
                e.preventDefault();
                e.stopPropagation();
                startSheetPan(e.clientX, e.clientY);
              }
            }}
          >
            {/* Внутренняя рамка листа по ГОСТ (20 мм слева для подшивки, по 5 мм сверху/справа/снизу) */}
            <div
              className="absolute border-2 border-black pointer-events-none"
              style={{
                left: '20mm',
                top: '5mm',
                right: '5mm',
                bottom: '5mm',
                boxSizing: 'border-box',
              }}
            >
              {/* Штамп листа (Основная надпись ГОСТ в правом нижнем углу) */}
              <GostStamp
                stampType={stampType}
                data={stampData}
                onDataChange={setStampData}
                isEditable={false}
              />
            </div>

            {/* Видовые экраны на листе */}
            {viewports.map((vp) => {
              const isSelected = !isExportingPdf && ((activeViewportId === vp.id) || (viewports.length === 1 && activeViewportId === null));
              const isFocused = !isExportingPdf && isSelected && isModelSpaceActive;
              const isVpLocked = !!vp.is_locked;

              // Rendered geometry
              const currentX = (vp.id === currentViewport?.id) ? geoX : vp.screen_x;
              const currentY = (vp.id === currentViewport?.id) ? geoY : vp.screen_y;
              const currentW = (vp.id === currentViewport?.id) ? geoW : vp.screen_width;
              const currentH = (vp.id === currentViewport?.id) ? geoH : vp.screen_height;

              return (
                <div
                  key={vp.id}
                  id={`viewport-box-${vp.id}`}
                  style={{
                    position: 'absolute',
                    left: `${currentX}mm`,
                    top: `${currentY}mm`,
                    width: `${currentW}mm`,
                    height: `${currentH}mm`,
                    boxSizing: 'border-box',
                    zIndex: isFocused ? 20 : (vp.z_index || 10),
                  }}
                  className={`relative group ${
                    isFocused
                      ? 'ring-2 ring-blue-500 shadow-xl'
                      : isSelected
                      ? 'ring-1 ring-blue-400 border border-blue-400'
                      : vp.show_frame
                      ? (isExportingPdf ? 'border border-black' : 'border border-gray-400')
                      : 'border border-transparent'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveViewportId(vp.id);
                  }}
                  onMouseDown={(e) => {
                    if (e.button === 1) {
                      e.preventDefault();
                      e.stopPropagation();
                      startSheetPan(e.clientX, e.clientY);
                    }
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setActiveViewportId(vp.id);
                    setIsModelSpaceActive(!isModelSpaceActive);
                  }}
                >
                  

                  {/* Бейдж состояния видового экрана */}
                  {!isExportingPdf && (
                    <div className="absolute top-1 left-1 z-30 flex items-center space-x-1 pointer-events-none">
                    {isFocused ? (
                      <span className="bg-blue-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow">
                        Model Space (активен)
                      </span>
                    ) : (
                      <span className="bg-gray-800/80 text-gray-200 text-[8px] px-1 py-0.2 rounded backdrop-blur">
                        Видовой экран
                      </span>
                    )}
                    {isVpLocked && (
                      <span className="bg-red-600 text-white text-[8px] p-0.5 rounded" title="Вид заблокирован">
                        <Lock className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </div>
                  )}

                  {/* Кнопка выхода из активного видового экрана */}
                  {!isExportingPdf && isFocused && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsModelSpaceActive(false);
                      }}
                      className="absolute top-1 right-1 z-30 bg-gray-800/90 hover:bg-gray-900 text-white text-[9px] px-2 py-0.5 rounded shadow transition pointer-events-auto"
                    >
                      Завершить (выйти на лист)
                    </button>
                  )}

                  {/* Рендерер схемы внутри видового экрана */}
                  <ViewportSchemaRenderer
                    projectId={parsedProjectId}
                    schemaId={vp.schema_id || selectedSchemaId}
                    schemaType={vp.schema_type || 'main'}
                    scrollX={vp.id === currentViewport?.id ? localPanX : vp.scroll_x}
                    scrollY={vp.id === currentViewport?.id ? localPanY : vp.scroll_y}
                    zoom={vp.id === currentViewport?.id ? localZoom : vp.zoom}
                    isInteractive={isFocused}
                    isLocked={isVpLocked}
                    onViewportChange={handleViewportPanZoomChange}
                    onBoundsComputed={handleBoundsComputed}
                  />

                  {/* Интерактивные маркеры изменения размера (Resize Handles) */}
                  {isSelected && !isFocused && (
                    <>
                      {/* Углы */}
                      <div
                        className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-blue-600 border border-white cursor-nwse-resize z-40 rounded-sm shadow"
                        onMouseDown={(e) => handleResizeStart(e, 'nw', vp)}
                      />
                      <div
                        className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-blue-600 border border-white cursor-nesw-resize z-40 rounded-sm shadow"
                        onMouseDown={(e) => handleResizeStart(e, 'ne', vp)}
                      />
                      <div
                        className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-blue-600 border border-white cursor-nesw-resize z-40 rounded-sm shadow"
                        onMouseDown={(e) => handleResizeStart(e, 'sw', vp)}
                      />
                      <div
                        className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-blue-600 border border-white cursor-nwse-resize z-40 rounded-sm shadow"
                        onMouseDown={(e) => handleResizeStart(e, 'se', vp)}
                      />

                      {/* Стороны */}
                      <div
                        className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-2 bg-blue-600 border border-white cursor-ns-resize z-40 rounded-sm shadow"
                        onMouseDown={(e) => handleResizeStart(e, 'n', vp)}
                      />
                      <div
                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-2 bg-blue-600 border border-white cursor-ns-resize z-40 rounded-sm shadow"
                        onMouseDown={(e) => handleResizeStart(e, 's', vp)}
                      />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-4 bg-blue-600 border border-white cursor-ew-resize z-40 rounded-sm shadow"
                        onMouseDown={(e) => handleResizeStart(e, 'w', vp)}
                      />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-4 bg-blue-600 border border-white cursor-ew-resize z-40 rounded-sm shadow"
                        onMouseDown={(e) => handleResizeStart(e, 'e', vp)}
                      />
                    </>
                  )}

                  {/* Неактивный оверлей (защита от случайного скролла листа / перемещение видового экрана) */}
                  {!isFocused && (
                    <div
                      className={`absolute inset-0 bg-transparent ${isSelected ? 'cursor-move' : 'cursor-pointer'}`}
                      onMouseDown={(e) => {
                        if (e.button === 1) {
                          e.preventDefault();
                          e.stopPropagation();
                          startSheetPan(e.clientX, e.clientY);
                          return;
                        }
                        if (isSelected && e.button === 0) {
                          handleResizeStart(e, 'move', vp);
                        }
                      }}
                      title={isSelected ? 'Потяните для перемещения видового экрана по листу' : 'Кликните для выбора или дважды щелкните для активации управления схемой'}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 3. Правая панель управления (AutoCAD Properties Palette) */}
        <aside className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col shrink-0 z-20">
          {/* Вкладки панели */}
          <div className="flex border-b border-gray-700 bg-gray-850 text-xs">
            <button
              onClick={() => setSidebarTab('viewport')}
              className={`flex-1 py-2.5 font-medium text-center border-b-2 transition ${
                sidebarTab === 'viewport'
                  ? 'border-blue-500 text-white bg-gray-800'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              Видовой экран
            </button>
            <button
              onClick={() => setSidebarTab('stamp')}
              className={`flex-1 py-2.5 font-medium text-center border-b-2 transition ${
                sidebarTab === 'stamp'
                  ? 'border-blue-500 text-white bg-gray-800'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              Штамп ГОСТ
            </button>
            <button
              onClick={() => setSidebarTab('sheet')}
              className={`flex-1 py-2.5 font-medium text-center border-b-2 transition ${
                sidebarTab === 'sheet'
                  ? 'border-blue-500 text-white bg-gray-800'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              Лист
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-5 text-xs text-gray-300">
            {/* ВКЛАДКА 1: ВИДОВОЙ ЭКРАН (VIEWPORT & PAN/ZOOM) */}
            {sidebarTab === 'viewport' && (
              <>
                {currentViewport ? (
                  <div className="space-y-4">
                    {/* Выбор схемы */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-300 mb-1">
                        Отображаемая схема:
                      </label>
                      <select
                        value={selectedSchemaId || ''}
                        onChange={(e) => handleSelectSchema(Number(e.target.value))}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-2.5 py-1.5 text-white outline-none focus:border-blue-500"
                      >
                        {projectSchemas.map((s) => (
                          <option key={s.id} value={s.id}>
                            #{s.id}: {s.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* БЛОК: РАЗМЕР И ПОЛОЖЕНИЕ НА ЛИСТЕ (ГЕОМЕТРИЯ) */}
                    <div className="bg-gray-750 p-3 rounded border border-gray-700 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white">Размер на листе:</span>
                        <button
                          onClick={() => handleFitViewportToSheet()}
                          className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-[10px] font-medium"
                          title="Растянуть на всю свободную область листа"
                        >
                          <Maximize2 className="w-3 h-3" />
                          <span>Растянуть на весь лист</span>
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">Ширина (мм):</label>
                          <input
                            type="number"
                            step="5"
                            min="30"
                            max={sheetWidthMm}
                            value={geoW}
                            onChange={(e) => setGeoW(Number(e.target.value))}
                            onBlur={() => applyGeometry(geoX, geoY, geoW, geoH)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white font-mono text-center outline-none focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">Высота (мм):</label>
                          <input
                            type="number"
                            step="5"
                            min="30"
                            max={sheetHeightMm}
                            value={geoH}
                            onChange={(e) => setGeoH(Number(e.target.value))}
                            onBlur={() => applyGeometry(geoX, geoY, geoW, geoH)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white font-mono text-center outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">Позиция X (мм):</label>
                          <input
                            type="number"
                            step="5"
                            value={geoX}
                            onChange={(e) => setGeoX(Number(e.target.value))}
                            onBlur={() => applyGeometry(geoX, geoY, geoW, geoH)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white font-mono text-center outline-none focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">Позиция Y (мм):</label>
                          <input
                            type="number"
                            step="5"
                            value={geoY}
                            onChange={(e) => setGeoY(Number(e.target.value))}
                            onBlur={() => applyGeometry(geoX, geoY, geoW, geoH)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white font-mono text-center outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Статус фокуса / активация Model Space */}
                    <div className="bg-gray-750 p-3 rounded border border-gray-700 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white">Режим камеры:</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          isModelSpaceActive ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                        }`}>
                          {isModelSpaceActive ? 'Model Space (активен)' : 'Paper Space'}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setActiveViewportId(currentViewport.id);
                          setIsModelSpaceActive(!isModelSpaceActive);
                        }}
                        className={`w-full py-1.5 rounded font-medium text-xs transition ${
                          isModelSpaceActive
                            ? 'bg-gray-600 hover:bg-gray-500 text-white'
                            : 'bg-blue-600 hover:bg-blue-500 text-white'
                        }`}
                      >
                        {isModelSpaceActive ? 'Выйти на лист (зафиксировать)' : 'Активировать схему для навигации'}
                      </button>
                      <p className="text-[10px] text-gray-400 leading-tight">
                        В активном режиме колесико мыши зумит схему, а drag перемещает её кадр.
                      </p>
                    </div>

                    {/* Точный числовой ввод Pan X / Pan Y */}
                    <div className="bg-gray-750 p-3 rounded border border-gray-700 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white">Смещение схемы (PAN):</span>
                        <button
                          onClick={() => applyManualPanZoom(0, 0, localZoom)}
                          className="text-[10px] text-blue-400 hover:underline"
                        >
                          Сбросить (0, 0)
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">X (px):</label>
                          <input
                            type="number"
                            step="10"
                            value={localPanX}
                            onChange={(e) => setLocalPanX(Number(e.target.value))}
                            onBlur={() => applyManualPanZoom(localPanX, localPanY, localZoom)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white font-mono text-center outline-none focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">Y (px):</label>
                          <input
                            type="number"
                            step="10"
                            value={localPanY}
                            onChange={(e) => setLocalPanY(Number(e.target.value))}
                            onBlur={() => applyManualPanZoom(localPanX, localPanY, localZoom)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white font-mono text-center outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Точный масштаб (ZOOM) */}
                    <div className="bg-gray-750 p-3 rounded border border-gray-700 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white">Масштаб (ZOOM):</span>
                        <span className="font-mono text-blue-400 font-bold">
                          {(localZoom * 100).toFixed(1)}%
                        </span>
                      </div>

                      {/* Быстрые кнопки масштаба */}
                      <div className="grid grid-cols-4 gap-1">
                        {[0.25, 0.5, 0.75, 1.0].map((z) => (
                          <button
                            key={z}
                            onClick={() => applyManualPanZoom(localPanX, localPanY, z)}
                            className={`py-1 rounded text-[10px] font-mono transition ${
                              Math.abs(localZoom - z) < 0.01
                                ? 'bg-blue-600 text-white font-bold'
                                : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                            }`}
                          >
                            {Math.round(z * 100)}%
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          step="0.05"
                          min="0.05"
                          max="5"
                          value={localZoom}
                          onChange={(e) => setLocalZoom(Number(e.target.value))}
                          onBlur={() => applyManualPanZoom(localPanX, localPanY, localZoom)}
                          className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white font-mono text-center outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={handleZoomExtents}
                          className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-200 text-[10px] font-medium transition"
                          title="Подогнать схему под видовой экран"
                        >
                          Вписать (Extents)
                        </button>
                      </div>
                    </div>

                    {/* Блокировка и параметры видового экрана */}
                    <div className="bg-gray-750 p-3 rounded border border-gray-700 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300">Заблокировать вид:</span>
                        <button
                          onClick={handleToggleLock}
                          className={`flex items-center space-x-1 px-2 py-1 rounded text-[10px] font-bold transition ${
                            isLocked ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'
                          }`}
                        >
                          {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                          <span>{isLocked ? 'Заблокирован' : 'Разблокирован'}</span>
                        </button>
                      </div>

                      <label className="flex items-center space-x-2 cursor-pointer pt-1">
                        <input
                          type="checkbox"
                          checked={!!currentViewport.show_frame}
                          onChange={(e) => saveViewport(currentViewport.id, { show_frame: e.target.checked ? 1 : 0 })}
                          className="rounded bg-gray-700 border-gray-600 text-blue-600"
                        />
                        <span className="text-gray-300 text-[11px]">Показывать рамку экрана при печати</span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <p>Нет видовых экранов на этом листе.</p>
                  </div>
                )}
              </>
            )}

            {/* ВКЛАДКА 2: ШТАМП ГОСТ (TITLE BLOCK) */}
            {sidebarTab === 'stamp' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-300 mb-1">
                    Тип штампа:
                  </label>
                  <select
                    value={stampType}
                    onChange={(e) => {
                      const newType = e.target.value as any;
                      setStampType(newType);
                      handleSaveStamp(newType, stampData);
                    }}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-2.5 py-1.5 text-white outline-none focus:border-blue-500"
                  >
                    <option value="gost_form1">ГОСТ 2.104 Форма 1 (185 × 55 мм, первый лист)</option>
                    <option value="gost_form2a">ГОСТ 2.104 Форма 2а (185 × 15 мм, последующие)</option>
                    <option value="simple">Простой заголовок</option>
                    <option value="none">Без штампа (чистая рамка)</option>
                  </select>
                </div>

                <div className="space-y-2.5">
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-0.5">Наименование объекта:</label>
                    <input
                      type="text"
                      value={stampData.projectName || ''}
                      onChange={(e) => setStampData(s => ({ ...s, projectName: e.target.value }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-0.5">Наименование чертежа:</label>
                    <input
                      type="text"
                      value={stampData.sheetTitle || ''}
                      onChange={(e) => setStampData(s => ({ ...s, sheetTitle: e.target.value }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-0.5">Шифр документа:</label>
                      <input
                        type="text"
                        value={stampData.docCode || ''}
                        onChange={(e) => setStampData(s => ({ ...s, docCode: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-0.5">Стадия:</label>
                      <input
                        type="text"
                        value={stampData.stage || 'Р'}
                        onChange={(e) => setStampData(s => ({ ...s, stage: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs outline-none focus:border-blue-500 text-center"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-0.5">Разработал:</label>
                      <input
                        type="text"
                        value={stampData.author || ''}
                        onChange={(e) => setStampData(s => ({ ...s, author: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-0.5">Проверил:</label>
                      <input
                        type="text"
                        value={stampData.checker || ''}
                        onChange={(e) => setStampData(s => ({ ...s, checker: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-0.5">Организация:</label>
                    <input
                      type="text"
                      value={stampData.company || 'BAGSUS'}
                      onChange={(e) => setStampData(s => ({ ...s, company: e.target.value }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                <button
                  onClick={() => handleSaveStamp(stampType, stampData)}
                  className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded text-xs transition shadow flex items-center justify-center space-x-1"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>Сохранить штамп</span>
                </button>
              </div>
            )}

            {/* ВКЛАДКА 3: ПАРАМЕТРЫ ЛИСТА (PAPER SETTINGS) */}
            {sidebarTab === 'sheet' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-300 mb-1">
                    Название листа:
                  </label>
                  <input
                    type="text"
                    value={sheetData?.name || ''}
                    onChange={async (e) => {
                      const newName = e.target.value;
                      await fetch(`/api/print/sheets/${parsedSheetId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ...sheetData, name: newName }),
                      });
                      queryClient.invalidateQueries({ queryKey: ['print-sheet', parsedSheetId] });
                    }}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-2.5 py-1.5 text-white text-xs outline-none focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">Формат бумаги:</label>
                    <select
                      value={sheetData?.format || 'A4'}
                      onChange={async (e) => {
                        const newFormat = e.target.value as any;
                        await fetch(`/api/print/sheets/${parsedSheetId}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ...sheetData, format: newFormat }),
                        });
                        queryClient.invalidateQueries({ queryKey: ['print-sheet', parsedSheetId] });
                        // Auto-fit viewport to new sheet format
                        handleFitViewportToSheet(newFormat, sheetData?.orientation);
                      }}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white outline-none focus:border-blue-500"
                    >
                      <option value="A4">A4 (297 × 210)</option>
                      <option value="A3">A3 (420 × 297)</option>
                      <option value="A2">A2 (594 × 420)</option>
                      <option value="A1">A1 (841 × 594)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">Ориентация:</label>
                    <select
                      value={sheetData?.orientation || 'landscape'}
                      onChange={async (e) => {
                        const newOrient = e.target.value as any;
                        await fetch(`/api/print/sheets/${parsedSheetId}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ...sheetData, orientation: newOrient }),
                        });
                        queryClient.invalidateQueries({ queryKey: ['print-sheet', parsedSheetId] });
                        // Auto-fit viewport to new sheet orientation
                        handleFitViewportToSheet(sheetData?.format, newOrient);
                      }}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white outline-none focus:border-blue-500"
                    >
                      <option value="landscape">Альбомная</option>
                      <option value="portrait">Книжная</option>
                    </select>
                  </div>
                </div>

                <div className="bg-gray-750 p-3 rounded border border-gray-700 space-y-1">
                  <div className="text-[11px] font-semibold text-white">Размеры чертежа:</div>
                  <div className="text-gray-400 text-[10px]">
                    Ширина: <span className="text-white font-mono">{sheetWidthMm} мм</span>
                  </div>
                  <div className="text-gray-400 text-[10px]">
                    Высота: <span className="text-white font-mono">{sheetHeightMm} мм</span>
                  </div>
                  <div className="text-gray-400 text-[10px]">
                    Поле подшивки: <span className="text-white font-mono">20 мм слева</span>
                  </div>
                </div>

                <button
                  onClick={() => handleFitViewportToSheet()}
                  className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded text-xs transition shadow flex items-center justify-center space-x-1.5"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span>Подогнать видовой экран под этот лист</span>
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
